import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { ISelectionDependentCommandBarItemProps } from "cayo.ui";
import { normalizePropertyNames } from "@cayo/admin-components/components/grids/utils";
import { IColumn } from "@cayo/shared/api/types/schema";
import useArray from "@cayo/shared/hooks/useArray";
import { commonMessages } from "@cayo/shared/messages/common";
import { joinToGroupsGridMessages } from "../messages";
import { IJoinToGroupsGridItem } from "../types";
import { formatJoinToGroupsPayload } from "../utils";

interface JoinToGroupsGridProps {
  columns: IColumn[] | undefined;
  setValue: (value: any) => void;
  readOnly?: boolean;
}

const idPropName = "id";

export const useJoinToGroupsGrid = ({ columns, setValue, readOnly }: JoinToGroupsGridProps) => {
  const intl = useIntl();
  const [isPickerVisible, setIsPickerVisible] = useState<boolean>(false);
  const { removeItems, array: items, addItems } = useArray<IJoinToGroupsGridItem>([], idPropName);
  const setValueRef = useRef(setValue);

  useEffect(() => {
    setValueRef.current = setValue;
  }, [setValue]);

  useEffect(() => {
    setValueRef.current(formatJoinToGroupsPayload(items));
  }, [items]);

  const handleAddItems = useCallback(
    (newItems: IJoinToGroupsGridItem[]) => {
      const normalized = normalizePropertyNames(newItems, columns);
      addItems(normalized);
    },
    [columns]
  );

  const handleShowPicker = useCallback(() => {
    setIsPickerVisible(true);
  }, []);

  const commands = useMemo(() => {
    if (readOnly) {
      return [];
    }

    const discoverGroups: ISelectionDependentCommandBarItemProps = {
      key: "discoverGroups",
      text: `${intl.formatMessage(joinToGroupsGridMessages.discoverGroups)}...`,
      iconProps: { iconName: "Add" },
      onClick: handleShowPicker,
    };

    const removeGroups: ISelectionDependentCommandBarItemProps = {
      key: "removeGroups",
      text: intl.formatMessage(commonMessages.remove),
      iconProps: { iconName: "Delete" },
      dependsOnSelection: true,
      multiselect: true,
      onClickWithSelection(groups) {
        const ids = groups?.map((item) => item[idPropName]);

        if (ids?.length) {
          removeItems(ids);
        }
      },
    };

    return [discoverGroups, removeGroups];
  }, [items, readOnly]);

  return {
    isPickerVisible,
    hidePicker: () => setIsPickerVisible(() => false),
    items,
    addItems: handleAddItems,
    commands,
  };
};
